import { Badge, getPaymentStatusBadge } from '@/components/ui/Badge';
import { formatCurrency } from '@/lib/utils';
import type { PurchaseWithTickets } from '@eventflow/shared-types';

interface PaymentListItemProps {
  purchase: PurchaseWithTickets;
  isSelected: boolean;
  onClick: () => void;
  deadlineInfo?: {
    label: string;
    isExpired: boolean;
    isExpiringSoon: boolean;
    icon: React.ReactNode;
  } | null;
}

export function PaymentListItem({
  purchase,
  isSelected,
  onClick,
  deadlineInfo,
}: PaymentListItemProps) {
  const hasProof = Boolean(purchase.payment_proof_url);
  const statusBadge = getPaymentStatusBadge(purchase.payment_status, hasProof);

  // Formatear fecha relativa
  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;

    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
    });
  };

  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left p-4 border-b border-gray-100 transition-all duration-150
        hover:bg-gray-50 focus:outline-none focus:bg-blue-50
        ${
          isSelected
            ? 'bg-blue-50 border-l-4 border-l-blue-500 hover:bg-blue-50'
            : 'border-l-4 border-l-transparent'
        }
        ${purchase.payment_status === 'pending' && hasProof ? 'bg-emerald-50/30' : ''}
        ${deadlineInfo?.isExpired ? 'bg-red-50/50' : ''}
        ${deadlineInfo?.isExpiringSoon && !deadlineInfo?.isExpired ? 'bg-amber-50/50' : ''}
      `}
    >
      {/* Header: Nombre y Monto */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 truncate text-sm">{purchase.customer_name}</h4>
          <p className="text-xs text-gray-500 truncate" title={purchase.customer_email}>
            {purchase.customer_email}
          </p>
          <p className="text-xs text-gray-400 truncate">{purchase.event.name}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="font-bold text-gray-900 text-sm">{formatCurrency(purchase.total_amount)}</p>
          <p className="text-xs text-gray-400">
            {purchase.tickets.length} asiento{purchase.tickets.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Footer: Badge y Tiempo */}
      <div className="flex items-center justify-between gap-2">
        <Badge variant={statusBadge.variant} size="sm">
          {statusBadge.icon} {statusBadge.label}
        </Badge>

        <div className="flex items-center gap-2">
          {/* Indicador de tiempo límite */}
          {deadlineInfo && purchase.payment_status === 'pending' && (
            <span
              className={`text-xs font-medium ${
                deadlineInfo.isExpired
                  ? 'text-red-600'
                  : deadlineInfo.isExpiringSoon
                    ? 'text-amber-600'
                    : 'text-gray-500'
              }`}
              title={deadlineInfo.label}
            >
              {deadlineInfo.icon}
            </span>
          )}
          <span className="text-xs text-gray-400">{getRelativeTime(purchase.created_at)}</span>
        </div>
      </div>

      {/* Indicador visual de prioridad */}
      {purchase.payment_status === 'pending' && hasProof && (
        <div className="mt-2 flex items-center gap-1 text-xs text-emerald-600 font-medium">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          Listo para revisar
        </div>
      )}
    </button>
  );
}
